import { Color3, type Mesh, MeshBuilder, Quaternion, StandardMaterial, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, ck, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, fm, noise, thump, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, ConstructFrame, FxCtx } from "../types.ts";

const V = { leap: 0 } as const;
const PK = { Dart: pk(HeroId.NoonKnight, 0), Blade: pk(HeroId.NoonKnight, 1), AnkhShot: pk(HeroId.NoonKnight, 2) } as const;
const ZK = { Talons: zk(HeroId.NoonKnight, 0) } as const;
const CK = { Ankh: ck(HeroId.NoonKnight, 0) } as const;
const FX = { Dart: fxk(HeroId.NoonKnight, 0), Moon: fxk(HeroId.NoonKnight, 1), Talon: fxk(HeroId.NoonKnight, 2) } as const;
const hz = (s: number) => 293.66 * 2 ** (s / 12);
const crescentCue: Cue = trim(16, (o) => {
  fm(o, { f: 2300, ratio: 1.7, index: 2, index1: 0, gain: 0.09, d: 0.09 });
  whoosh(o, 0.2, 1200, 3400, 0.1);
  return 0.18;
});
const verdictCue: Cue = trim(-3, (o) => {
  thump(o, 0.7, 92, 32, 0.55);
  noise(o, { color: "pink", type: "bandpass", f: 650, f1: 2600, gain: 0.45, d: 0.7 });
  bell(o, hz(19), 0.5, 0.6);
  return 1.2;
});
const theme = {
  bpm: 104,
  octave: 0,
  voice: (o, f, d, v) => bell(o, f, d, v),
  double: (o, f, d, v) => fm(o, { f: f / 2, ratio: 1.5, index: 2, index1: 0, gain: v * 0.1, d }),
  notes: [
    [0, 0.5],
    [7, 0.5],
    [12, 0.5],
    [19, 1.5],
  ],
} satisfies ClientHero["audio"]["theme"];

type NoonGear = { cape: Mesh; wings: Mesh[]; hood: Mesh };

const hero: ClientHero<NoonGear> = {
  id: HeroId.NoonKnight,
  bulk: 1.0,
  shape: { head: 0.92, torso: 1.0, arms: 1.05, legs: 1.12 },
  gear(g) {
    const { R, H, scene, add, rig, glow, toon, dark } = g;
    const hood = add(MeshBuilder.CreateCylinder("noon-hood", { diameterTop: R * 1.05, diameterBottom: R * 1.35, height: 0.28, tessellation: 12 }, scene), rig.head, toon("#ffffff"));
    hood.position.y = R * 0.45;
    const cape = add(MeshBuilder.CreateBox("noon-cape", { width: R * 1.7, height: H * 0.55, depth: 0.05 }, scene), rig.torso, toon("#ffffff"));
    cape.position.set(0, -H * 0.08, R * 0.7);
    const wings: Mesh[] = [];
    for (const side of [-1, 1]) {
      const wing = add(MeshBuilder.CreateBox("glider-wing", { width: R * 0.95, height: 0.055, depth: H * 0.42 }, scene), rig.torso, toon("#f3f1e8"));
      wing.position.set(side * R * 0.72, -H * 0.04, R * 0.72);
      wing.rotation.z = side * 0.22;
      wings.push(wing);
    }
    for (const hand of [rig.handL, rig.handR]) {
      const blade = add(MeshBuilder.CreateTorus("crescent", { diameter: 0.34, thickness: 0.035, tessellation: 16 }, scene), hand, glow("#fff3a6"));
      blade.position.z = -0.3;
      blade.scaling.x = 0.65;
    }
    const baton = add(MeshBuilder.CreateBox("baton", { width: 0.1, height: 0.1, depth: 0.85 }, scene), rig.handR, dark);
    baton.position.z = -0.32;
    return { cape, wings, hood };
  },
  animate(gear, f) {
    const gliding = !f.grounded && !!f.speed;
    gear.cape.rotation.x = 0.1 + (gliding ? 0.25 : 0) + Math.sin(f.t * 5) * 0.025;
    for (const [i, wing] of gear.wings.entries()) wing.rotation.z = (i === 0 ? -1 : 1) * (0.22 + (gliding ? 0.42 : 0));
    gear.hood.rotation.y = Math.sin(f.t * 2) * 0.015;
    if (!f.grounded && (f.v[V.leap] > 0 || f.speed > 3)) return "holding-both";
    return undefined;
  },
  hud(c, eid) {
    return [{ label: "Leap", value: hv(c, eid, V.leap), color: "#fff3a6" }];
  },
  lifting(_c, _eid, buttons, grounded) {
    return !grounded && !!(buttons & Btn.Jump);
  },
  constructs: {
    [CK.Ankh]: (scene, _f: ConstructFrame) => {
      const root = MeshBuilder.CreateBox("ankh-stem", { width: 0.16, height: 1.4, depth: 0.12 }, scene);
      root.material = new StandardMaterial("ankh-mat", scene);
      (root.material as StandardMaterial).emissiveColor = Color3.FromHexString("#ffd85c");
      const loop = MeshBuilder.CreateTorus("ankh-loop", { diameter: 0.6, thickness: 0.08, tessellation: 24 }, scene);
      loop.material = root.material;
      loop.parent = root;
      loop.position.y = 0.45;
      const arm = MeshBuilder.CreateBox("ankh-arm", { width: 0.72, height: 0.1, depth: 0.1 }, scene);
      arm.material = root.material;
      arm.parent = root;
      arm.position.y = 0.15;
      const sun = MeshBuilder.CreateTorus("ankh-sun", { diameter: 1.05, thickness: 0.035, tessellation: 28 }, scene);
      sun.material = root.material;
      sun.parent = root;
      sun.position.y = 0.16;
      return {
        gear: { root, loop, arm, sun },
        update(_g, fr) {
          root.position.set(fr.pos.x, fr.pos.y + 0.75, fr.pos.z);
          root.rotation.y = fr.yaw + (720 - fr.ticks) * 0.002;
          root.visibility = 0.35 + fr.hpFrac * 0.65;
        },
        dispose() {
          root.dispose();
        },
      };
    },
  },
  projectiles: {
    [PK.Dart]: (scene, f) => crescent(scene, f.pos, 0.28),
    [PK.Blade]: (scene, f) => crescent(scene, f.pos, 0.48),
    [PK.AnkhShot]: (scene, f) => crescent(scene, f.pos, 0.36),
  },
  zones: {
    [ZK.Talons]: (scene, f) => {
      const ring = MeshBuilder.CreateTorus("talon-field", { diameter: f.radius * 2, thickness: 0.08, tessellation: 48 }, scene);
      ring.material = new StandardMaterial("talon-field-mat", scene);
      (ring.material as StandardMaterial).emissiveColor = Color3.FromHexString("#fff3a6");
      (ring.material as StandardMaterial).alpha = 0.42;
      return {
        gear: ring,
        update(_mesh, fr) {
          ring.position.set(fr.pos.x, fr.pos.y + 0.08, fr.pos.z);
          ring.rotation.y += fr.dt;
          ring.scaling.setAll(fr.ticks > 270 ? 0.7 + fr.age / 1.5 : 1 + Math.sin(fr.age * 20) * 0.04);
        },
        icons: {
          [Slot.Primary]: "M5 12 L19 7 M5 12 L19 12 M5 12 L19 17",
          [Slot.Secondary]: "M4 12 C7 5 17 5 20 12 C17 19 7 19 4 12",
          [Slot.Ability1]: "M3 8 C8 5 16 5 21 8 L17 18 L12 14 L7 18 Z",
          [Slot.Ability2]: "M12 3 V21 M7 9 H17 M8 15 C8 11 16 11 16 15",
          [Slot.Ability3]: "M5 17 L12 6 L19 17 M8 14 H16",
          [Slot.Ultimate]: "M12 3 L15 10 L22 12 L15 14 L12 21 L9 14 L2 12 L9 10 Z",
          passive: "M4 15 C8 8 16 8 20 15 M6 18 C10 14 14 14 18 18",
        },
        dispose: () => ring.dispose(),
      };
    },
  },
  fx: {
    [FX.Dart]: { impact: (c, pos) => spark(c, pos, "#fff3a6") },
    [FX.Moon]: { explosion: (c, pos, radius) => sunRing(c, pos, radius) },
    [FX.Talon]: { explosion: (c, pos, radius) => sunRing(c, pos, radius) },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "noon-blade" : "noon-dart", cue: crescentCue }),
    reload: (seconds) => trim(14, (o) => {
      noise(o, { color: "pink", type: "bandpass", f: 1500, gain: 0.15, d: seconds });
      return seconds + 0.1;
    }),
    ability: {
      [Slot.Secondary]: crescentCue,
      [Slot.Ability1]: crescentCue,
      [Slot.Ability2]: verdictCue,
      [Slot.Ability3]: crescentCue,
      [Slot.Ultimate]: verdictCue,
    },
    impacts: { [FX.Dart]: crescentCue, [FX.Moon]: verdictCue, [FX.Talon]: verdictCue },
    explosions: { [FX.Moon]: () => verdictCue, [FX.Talon]: () => verdictCue },
    flight: { [PK.Dart]: loops.flight("missile"), [PK.Blade]: loops.flight("missile"), [PK.AnkhShot]: loops.flight("orb") },
    constructLoops: { [CK.Ankh]: { build: loops.beacon, gain: 0.55, range: 26 } },
    zoneLoops: { [ZK.Talons]: { build: loops.barrage, gain: 0.8, range: 42 } },
    selfLift: ({ alive }) => (alive ? loops.levitate : undefined),
    theme,
    themeTrim: { heroic: 2.2, hostile: -5.8, tease: 6.7 },
  },
};

function crescent(scene: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], pos: { x: number; y: number; z: number }, size: number) {
  const m = MeshBuilder.CreateTorus("crescent-proj", { diameter: size, thickness: size * 0.12, tessellation: 18 }, scene);
  m.material = new StandardMaterial("crescent-proj-mat", scene);
  (m.material as StandardMaterial).emissiveColor = Color3.FromHexString("#fff3a6");
  const core = MeshBuilder.CreateSphere("crescent-sun", { diameter: size * 0.25, segments: 8 }, scene);
  core.material = m.material;
  core.parent = m;
  m.scaling.x = 0.62;
  return {
    gear: m,
    update(_mesh: Mesh, fr: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[1]) {
      m.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
      m.rotation.y += fr.dt * 18;
      m.lookAt(m.position.add(new Vector3(fr.dir.x, fr.dir.y, fr.dir.z)));
    },
    dispose: () => m.dispose(),
  };
}

function spark(c: FxCtx, pos: { x: number; y: number; z: number }, color: string) {
  const m = MeshBuilder.CreateSphere("noon-spark", { diameter: 0.45, segments: 8 }, c.scene);
  m.material = c.glow(color);
  m.position.set(pos.x, pos.y, pos.z);
  c.timed(m, 0.18, (t, mesh) => {
    mesh.scaling.setAll(1 + t * 2);
    mesh.visibility = 1 - t;
  });
}

function sunRing(c: FxCtx, pos: { x: number; y: number; z: number }, radius: number) {
  const m = MeshBuilder.CreateTorus("sun-ring", { diameter: 2, thickness: 0.16, tessellation: 40 }, c.scene);
  m.material = c.glow("#fff3a6", 0.6);
  m.position.set(pos.x, pos.y + 0.08, pos.z);
  c.timed(m, 0.32, (t, mesh) => {
    mesh.scaling.setAll(radius * (0.15 + t));
    mesh.visibility = 1 - t;
  });
}

void Quaternion;
export default hero;
